// File: src/components/admin/DocumentationConfigView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// Documentation Configuration View - manage markdown documents for AI prompts

import React, { useState, useEffect } from 'react';
import {
    Box, Button, Card, CardContent, CircularProgress,
    Alert, Snackbar, Typography, Grid,
    TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Switch, FormControlLabel, Chip
} from '@mui/material';
import { Save, Add, Delete, Edit, Description } from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../services/adminApi';

const DocumentationConfigView = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Dialog state for add/edit
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDoc, setEditingDoc] = useState(null);
    const [docForm, setDocForm] = useState({ name: '', content: '', enabled: true });

    useEffect(() => {
        loadDocumentation();
    }, []);

    const loadDocumentation = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminApi.getDocumentation();
            setDocuments(data.documents || []);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setError(errorInfo);
            setSnackbar({ open: true, message: errorInfo.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const updated = await adminApi.updateDocumentation({ documents });
            setDocuments(updated.documents || []);
            setSnackbar({ open: true, message: 'Documentation saved successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Save failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleOpenDialog = (doc = null) => {
        if (doc) {
            setEditingDoc(doc);
            setDocForm({ name: doc.name, content: doc.content, enabled: doc.enabled });
        } else {
            setEditingDoc(null);
            setDocForm({ name: '', content: '', enabled: true });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingDoc(null);
        setDocForm({ name: '', content: '', enabled: true });
    };

    const handleSaveDocument = () => {
        if (!docForm.name.trim() || !docForm.content.trim()) {
            setSnackbar({ open: true, message: 'Name and content are required', severity: 'warning' });
            return;
        }

        if (editingDoc) {
            // Update existing
            setDocuments(prev => prev.map(d =>
                d.id === editingDoc.id
                    ? { ...d, name: docForm.name, content: docForm.content, enabled: docForm.enabled, updatedAt: new Date().toISOString() }
                    : d
            ));
        } else {
            // Add new
            const newDoc = {
                id: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                name: docForm.name,
                content: docForm.content,
                enabled: docForm.enabled,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            setDocuments(prev => [...prev, newDoc]);
        }
        handleCloseDialog();
    };

    const handleDeleteDocument = (docId) => {
        if (window.confirm('Are you sure you want to delete this document?')) {
            setDocuments(prev => prev.filter(d => d.id !== docId));
        }
    };

    const handleToggleEnabled = (docId) => {
        setDocuments(prev => prev.map(d =>
            d.id === docId ? { ...d, enabled: !d.enabled } : d
        ));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Documentation</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Configure markdown documents that will be included in AI prompts for subscription analysis.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add Document
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save All'}
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error.message}
                </Alert>
            )}

            {documents.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Description sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary">
                            No documentation configured
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Add markdown documents to provide context to AI analysis prompts.
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenDialog()}
                        >
                            Add Your First Document
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {documents.map((doc) => (
                        <Grid item xs={12} md={6} key={doc.id}>
                            <Card variant="outlined" sx={{
                                opacity: doc.enabled ? 1 : 0.6,
                                borderColor: doc.enabled ? 'divider' : 'action.disabled'
                            }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Description color={doc.enabled ? 'primary' : 'disabled'} sx={{ mr: 1 }} />
                                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                            {doc.name}
                                        </Typography>
                                        <Switch
                                            size="small"
                                            checked={doc.enabled}
                                            onChange={() => handleToggleEnabled(doc.id)}
                                            title={doc.enabled ? 'Enabled - will be included in prompts' : 'Disabled'}
                                        />
                                        <IconButton size="small" onClick={() => handleOpenDialog(doc)} title="Edit">
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteDocument(doc.id)} title="Delete">
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            maxHeight: 150,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            bgcolor: 'action.hover',
                                            p: 1,
                                            borderRadius: 1
                                        }}
                                    >
                                        {doc.content.substring(0, 500)}{doc.content.length > 500 ? '...' : ''}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                        <Chip
                                            label={doc.enabled ? 'Active' : 'Disabled'}
                                            size="small"
                                            color={doc.enabled ? 'success' : 'default'}
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={`${doc.content.length} chars`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingDoc ? 'Edit Document' : 'Add Document'}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Document Name"
                                value={docForm.name}
                                onChange={(e) => setDocForm({ ...docForm, name: e.target.value })}
                                placeholder="e.g., Pricing Strategy, Feature Requirements, Brand Guidelines"
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={docForm.enabled}
                                        onChange={(e) => setDocForm({ ...docForm, enabled: e.target.checked })}
                                    />
                                }
                                label="Include in AI prompts"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Content (Markdown)"
                                value={docForm.content}
                                onChange={(e) => setDocForm({ ...docForm, content: e.target.value })}
                                multiline
                                rows={15}
                                placeholder={`# Document Title

## Section 1
Your markdown content here...

- Bullet points
- Requirements
- Guidelines

## Section 2
More content...`}
                                required
                                sx={{
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem'
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSaveDocument}
                        variant="contained"
                        disabled={!docForm.name.trim() || !docForm.content.trim()}
                    >
                        {editingDoc ? 'Update' : 'Add'} Document
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
            />
        </Box>
    );
};

export default DocumentationConfigView;
